import { Stop } from "@/common/constants/online.homepagegateway.api";
import { Button, Typography } from "@chr/eds-react";
import { useState } from "react";
import { toLocaleDateTime, toLocaleAddress, useTranslation } from '@chr/online-shared-ui-localizations';
import { useMediaQuery, useTheme } from "@mui/material";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';

export interface LocationInfoProps {
    stops: Stop[]
}

const LocationInfo = ({stops}: LocationInfoProps) => {
    const translate = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const onChange = () => setExpanded(!expanded);
    const theme = useTheme();
    const isSmOrSmaller = useMediaQuery(theme.breakpoints.down('md'));

    const getCityStateScheduledDateText = (location: Stop) => {
        let dateDisplay= '';
        let date = location.requestedDate;
        if (location.actualDate) {
            date = location.actualDate;
        } else if (location.scheduledDate) {
            date = location.scheduledDate;
        }

        if (date) {
            const newDate = new Date(date);
            dateDisplay = `on ${toLocaleDateTime(newDate, { dateStyle: 'medium' })} at ${toLocaleDateTime(newDate, { timeStyle: 'short' })}`;
        }

        return `${toLocaleAddress(null, null, location.postalCode, location.city, location.stateProvinceCode, location.countryCode, 'long', ', ')} ${dateDisplay}`;
    }

    const getSmallScheduledDateText = (location: Stop) => {
        let dateDisplay= '';
        let startText = location.stopType === 'Pick' ? translate('Requested pickup on') : translate('Delivery scheduled');
        let date = location.requestedDate;
        if (location.actualDate) {
            date = location.actualDate;
            startText = location.stopType === 'Pick' ? translate('Picked on') : translate('Delivered');
        } else if (location.scheduledDate) {
            date = location.scheduledDate;
        }
        
        if (date) {
            const newDate = new Date(date);
            dateDisplay = `${startText} ${toLocaleDateTime(newDate, { dateStyle: 'medium' })} ${toLocaleDateTime(newDate, { timeStyle: 'short' })}`;
        }

        return dateDisplay;
    }
    
    const renderAllLocations = () => {
        return (
            <div className="render-all">
                {stops.map((stop) => {
                    return (
                        <div key={`${stop.stopType}-${stop.id}`}>
                            {renderLocationColumn(stop)}
                            {<hr></hr>}
                        </div>
                    )
                })}
            </div>
        )
    }

    const renderLocationColumn = (stop: Stop) => {
        const isPick = stop.stopType === 'Pick';

        if (isSmOrSmaller) {
            return (
                <div className="from-to-column">
                    {isPick ?
                        <div>
                            <CheckCircleOutlineIcon 
                                sx={{ color: '#0071C7' }}
                            />
                            <div className="route-bread-crumb" />
                        </div>
                        :
                        <LocationOnOutlinedIcon 
                            sx={{ color: '#868A8D' }}
                        />
                    }
                    <div className="city-state-span">
                        <div className="date-span">
                            <Typography variant="subtitle400" fontWeight="semiBold">
                                {getSmallScheduledDateText(stop)}
                            </Typography>
                        </div>
                        <Typography variant="body100" fontWeight="regular">
                            {stop.locationName}
                        </Typography>
                        <div className="address-span">
                            <Typography variant="body100" fontWeight="regular">
                                {toLocaleAddress('', '', '', stop.city, stop.stateProvinceName, '', 'short', ', ')}
                            </Typography>
                        </div>
                    </div>
                </div>
            )
        }

        return (
            <div className="from-to-column">
                <p className="address-span" title={stop.locationName}>
                    {stop.locationName}
                </p>
                <p className="city-state-span" title={getCityStateScheduledDateText(stop)}>
                    {getCityStateScheduledDateText(stop)}
                </p>
            </div>
        )
    }
    
    if (stops.length == 0) {
        return (<div> - </div>)
    }
    return (
        <div>
            {
                expanded ? renderAllLocations() : renderLocationColumn(stops[0])
            }
            {
                stops.length > 1 &&
                <Button
                    startIcon={expanded ? 'expand_less' : 'expand_more'}
                    variant="tertiary"
                    buttonText={`${translate(expanded ? 'View Less' : 'View More')} (${stops.length - 1})`}
                    onClick={onChange} />
            }
        </div>
    );
}

export default LocationInfo;